/* Giveaway #4 inner-page enhancements. Draft theme only. */
(() => {
  const VIDEO_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;

  const initWinnerVideoDialog = () => {
    const dialog = document.querySelector('#G4VideoDialog');
    if (!dialog) return;

    const frame = dialog.querySelector('[data-g4-video-frame]');
    const title = dialog.querySelector('[data-g4-video-dialog-title]');
    const closeButton = dialog.querySelector('[data-g4-video-close]');
    let activeTrigger = null;

    if (!frame || !title || !closeButton) return;

    const clearPlayer = () => {
      frame.replaceChildren();
    };

    const returnFocus = () => {
      if (activeTrigger && document.contains(activeTrigger)) {
        activeTrigger.focus({ preventScroll: true });
      }
      activeTrigger = null;
    };

    const closeDialog = () => {
      if (dialog.open && typeof dialog.close === 'function') {
        dialog.close();
        return;
      }

      dialog.removeAttribute('open');
      clearPlayer();
      returnFocus();
    };

    const openDialog = (trigger) => {
      const videoId = trigger.dataset.g4YoutubeId || '';
      if (!VIDEO_ID_PATTERN.test(videoId)) return false;

      const requestedStart = Number.parseInt(trigger.dataset.g4YoutubeStart || '0', 10);
      const start = Number.isFinite(requestedStart) && requestedStart > 0
        ? `&start=${requestedStart}`
        : '';
      const iframe = document.createElement('iframe');

      iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0${start}`;
      iframe.title = trigger.dataset.g4VideoTitle || 'Shift Culture winner video';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';

      title.textContent = trigger.dataset.g4VideoTitle || 'Winner reveal';
      frame.replaceChildren(iframe);
      activeTrigger = trigger;

      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      closeButton.focus({ preventScroll: true });
      return true;
    };

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-g4-youtube-id]');
      if (!trigger) return;
      if (!openDialog(trigger)) return;
      event.preventDefault();
    });

    closeButton.addEventListener('click', closeDialog);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });
    dialog.addEventListener('close', () => {
      clearPlayer();
      returnFocus();
    });
  };

  // One contained photo viewer for every storefront route. Never forward a
  // product-image ID to the theme's different media-ID/fullscreen implementation.
  const initPhotoViewer = () => {
    if (document.documentElement.dataset.g4PhotoViewerReady) return;
    document.documentElement.dataset.g4PhotoViewerReady = 'true';

    const photoURL = (link) => {
      if (!link || link.hasAttribute('download') || link.hasAttribute('data-g4-youtube-id')) return null;
      try {
        const url = new URL(link.getAttribute('href'), window.location.href);
        if (!/^https?:$/.test(url.protocol)) return null;
        return /\.(avif|gif|jpe?g|png|svg|webp)$/i.test(url.pathname) ? url : null;
      } catch (_) { return null; }
    };
    const photoKey = (url) => `${url.origin}${url.pathname}`;
    const describe = (link) => {
      const image = link.querySelector('img') || link.closest('.product-single__media')?.querySelector('img');
      return (image?.alt || document.querySelector('h1')?.textContent || 'Product photo').split('#')[0].trim();
    };
    let dialog, image, title, count, status, previous, next, closeButton;
    let items = [], index = 0, trigger = null, escapeHandled = false;

    const renderPhoto = () => {
      const item = items[index];
      dialog.classList.add('g4-photo-dialog--loading');
      status.hidden = false;
      status.textContent = 'Loading photo…';
      title.textContent = item.alt || 'Product photo';
      image.alt = item.alt || 'Product photo';
      image.src = item.src;
      count.textContent = `${index + 1} / ${items.length}`;
      previous.hidden = next.hidden = items.length < 2;
      previous.disabled = index === 0;
      next.disabled = index === items.length - 1;
    };
    const restorePage = () => {
      document.documentElement.classList.remove('g4-photo-open');
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
      trigger = null;
      image.removeAttribute('src');
      items = [];
    };
    const closeViewer = () => {
      if (!dialog?.open) return;
      dialog.close();
    };
    const step = (amount) => {
      const selected = index + amount;
      if (selected < 0 || selected >= items.length) return;
      index = selected;
      renderPhoto();
    };
    const createViewer = () => {
      dialog = document.createElement('dialog');
      dialog.id = 'G4PhotoDialog';
      dialog.className = 'g4-photo-dialog';
      dialog.setAttribute('aria-labelledby', 'G4PhotoTitle');
      dialog.setAttribute('aria-describedby', 'G4PhotoCount');
      dialog.innerHTML = `
        <div class="g4-photo-dialog__header">
          <p id="G4PhotoTitle"></p>
          <button type="button" class="g4-photo-dialog__close" aria-label="Close photo viewer">
            <span>Close</span><svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>
          </button>
        </div>
        <div class="g4-photo-dialog__media">
          <img class="g4-photo-dialog__image" alt="" decoding="async">
          <p class="g4-photo-dialog__status" role="status"></p>
        </div>
        <div class="g4-photo-dialog__footer">
          <button type="button" class="g4-photo-dialog__previous" aria-label="Previous photo"><span aria-hidden="true">&#8592;</span> Previous</button>
          <span id="G4PhotoCount" aria-live="polite" aria-atomic="true"></span>
          <button type="button" class="g4-photo-dialog__next" aria-label="Next photo">Next <span aria-hidden="true">&#8594;</span></button>
        </div>`;
      document.body.append(dialog);
      image = dialog.querySelector('img');
      title = dialog.querySelector('#G4PhotoTitle');
      count = dialog.querySelector('#G4PhotoCount');
      status = dialog.querySelector('[role="status"]');
      previous = dialog.querySelector('.g4-photo-dialog__previous');
      next = dialog.querySelector('.g4-photo-dialog__next');
      closeButton = dialog.querySelector('.g4-photo-dialog__close');
      image.addEventListener('load', () => { dialog.classList.remove('g4-photo-dialog--loading'); status.hidden = true; });
      image.addEventListener('error', () => { status.textContent = 'This photo could not load. Close the viewer and try again.'; });
      closeButton.addEventListener('click', closeViewer);
      previous.addEventListener('click', () => step(-1));
      next.addEventListener('click', () => step(1));
      dialog.addEventListener('close', restorePage);
      dialog.addEventListener('cancel', event => { event.preventDefault(); closeViewer(); });
      let backdropStart = false;
      dialog.addEventListener('pointerdown', event => { backdropStart = event.target === dialog; });
      dialog.addEventListener('click', event => { if (backdropStart && event.target === dialog) closeViewer(); });
      dialog.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          event.preventDefault(); event.stopPropagation(); escapeHandled = true; closeViewer();
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); event.stopPropagation(); step(event.key === 'ArrowLeft' ? -1 : 1);
        } else if (event.key === 'Tab') {
          const controls = [...dialog.querySelectorAll('button')].filter(button => !button.hidden && !button.disabled);
          const first = controls[0], last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }
      });
      // Prevent unrelated legacy cart/search Escape handlers from taking focus.
      document.addEventListener('keyup', event => {
        if (event.key === 'Escape' && escapeHandled) { escapeHandled = false; event.stopImmediatePropagation(); }
      }, true);
      let touchStart = null;
      image.addEventListener('touchstart', event => {
        touchStart = event.touches.length === 1 ? { x:event.touches[0].clientX, y:event.touches[0].clientY } : null;
      }, { passive:true });
      image.addEventListener('touchend', event => {
        if (!touchStart || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - touchStart.x, dy = event.changedTouches[0].clientY - touchStart.y;
        if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.5) step(dx < 0 ? 1 : -1);
        touchStart = null;
      }, { passive:true });
    };
    document.addEventListener('click', event => {
      const link = event.target.closest?.('a[href]');
      const selected = photoURL(link);
      if (!selected) return;
      // Capture before the theme's PhotoSwipe handler, including dynamically
      // inserted quick views. Product/category/video links do not match.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!dialog) createViewer();
      const gallery = link.closest('.product-gallery-container, [data-photo-gallery], .image-gallery');
      const galleryLinks = gallery ? [...gallery.querySelectorAll('a[href]')].filter(anchor => photoURL(anchor)) : [link];
      const seen = new Set();
      items = galleryLinks.filter(anchor => {
        const key = photoKey(photoURL(anchor));
        if (seen.has(key)) return false;
        seen.add(key); return true;
      }).map(anchor => ({ src:photoURL(anchor).href, key:photoKey(photoURL(anchor)), alt:describe(anchor) }));
      index = Math.max(0, items.findIndex(item => item.key === photoKey(selected)));
      trigger = link;
      renderPhoto();
      document.documentElement.classList.add('g4-photo-open');
      if (!dialog.open) dialog.showModal();
      closeButton.focus({ preventScroll:true });
    }, true);
  };

  const readCartResponse = async (response) => {
    let payload = {};
    try {
      payload = await response.json();
    } catch (error) {
      payload = {};
    }

    if (!response.ok) {
      throw new Error(
        payload.description
        || payload.message
        || 'The cart could not be updated. Please try again.'
      );
    }
    return payload;
  };

  const postCartJson = async (url, payload) => {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    return readCartResponse(response);
  };

  const getCartJson = async (url) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    return readCartResponse(response);
  };

  const setOfferBusy = (button, busy, busyLabel) => {
    if (!button.dataset.g4IdleLabel) {
      button.dataset.g4IdleLabel = button.textContent.trim();
    }
    button.disabled = busy;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
    button.textContent = busy ? busyLabel : button.dataset.g4IdleLabel;
  };

  const initCartUpgrade = () => {
    const lowerHandles = new Set(['standard-instant-entry', 'premium-instant-entry']);
    const higherHandles = new Set(['ultimate-instant-entry', 'elite-quick-entry', 'elite-quick-entry-copy']);

    document.addEventListener('click', async (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-g4-cart-upgrade]');
      if (!button || button.getAttribute('aria-busy') === 'true') return;
      event.preventDefault();

      const status = button.closest('.g4-cart-offer')?.querySelector('[data-g4-cart-upgrade-status]');
      const cartUrl = button.dataset.g4CartUrl || '';
      const addUrl = button.dataset.g4CartAddUrl || '';
      const changeUrl = button.dataset.g4CartChangeUrl || '';
      const sourceKey = button.dataset.g4SourceKey || '';
      const sourceHandle = button.dataset.g4SourceHandle || '';
      const targetVariant = Number.parseInt(button.dataset.g4TargetVariant || '', 10);

      if (!cartUrl || !addUrl || !changeUrl || !sourceKey || !lowerHandles.has(sourceHandle) || !Number.isInteger(targetVariant)) {
        if (status) status.textContent = 'Please open the $100 pack to complete this upgrade.';
        return;
      }

      setOfferBusy(button, true, 'Upgrading...');
      if (status) status.textContent = '';

      try {
        const currentCart = await getCartJson(cartUrl);
        const lowerLines = currentCart.items.filter((item) => lowerHandles.has(item.handle));
        const hasHigherPack = currentCart.items.some((item) => higherHandles.has(item.handle));
        const liveSource = lowerLines[0];
        const hasProperties = liveSource
          ? Object.keys(liveSource.properties || {}).length > 0
          : false;

        if (
          lowerLines.length !== 1
          || !liveSource
          || liveSource.key !== sourceKey
          || liveSource.handle !== sourceHandle
          || liveSource.quantity !== 1
          || liveSource.selling_plan_allocation
          || hasProperties
          || hasHigherPack
        ) {
          throw new Error('Your cart changed. Refresh the page before upgrading.');
        }

        await postCartJson(addUrl, {
          items: [{ id: targetVariant, quantity: 1 }]
        });

        const cartAfterAdd = await getCartJson(cartUrl);
        const addedTarget = cartAfterAdd.items.find((item) => item.variant_id === targetVariant);
        if (!addedTarget) {
          throw new Error('The $100 pack could not be added. Your original pack is still in the cart.');
        }

        try {
          await postCartJson(changeUrl, { id: liveSource.key, quantity: 0 });
        } catch (changeError) {
          try {
            await postCartJson(changeUrl, { id: addedTarget.key, quantity: 0 });
          } catch (rollbackError) {
            window.location.reload();
            return;
          }
          throw changeError;
        }

        if (status) status.textContent = 'Upgraded to the $100 Best Value pack.';
        window.location.reload();
      } catch (error) {
        if (status) status.textContent = error.message;
        setOfferBusy(button, false, 'Upgrading...');
      }
    });
  };

  const initCartAddon = () => {
    document.addEventListener('change', (event) => {
      if (!(event.target instanceof Element)) return;
      const select = event.target.closest('[data-g4-cart-addon-variant]');
      if (!select) return;
      const selected = select.options[select.selectedIndex];
      const image = select.closest('.g4-cart-offer')?.querySelector('.g4-cart-addon__media img');
      if (!image || !selected?.dataset.g4VariantImage) return;
      image.src = selected.dataset.g4VariantImage;
      image.alt = selected.dataset.g4VariantAlt || image.alt;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
    });

    document.addEventListener('submit', async (event) => {
      if (!(event.target instanceof Element)) return;
      const form = event.target.closest('[data-g4-cart-addon-form]');
      if (!form) return;
      event.preventDefault();

      const button = form.querySelector('button[type="submit"]');
      const status = form.closest('.g4-cart-offer')?.querySelector('[data-g4-cart-addon-status]');
      let addUrl = form.dataset.g4CartAddUrl || form.action;
      if (!addUrl.endsWith('.js')) addUrl += '.js';
      if (!button || button.getAttribute('aria-busy') === 'true') return;

      setOfferBusy(button, true, 'Adding...');
      if (status) status.textContent = '';

      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        await readCartResponse(response);
        if (status) status.textContent = 'Liberty Walk BRZ tee added.';
        window.location.reload();
      } catch (error) {
        if (status) status.textContent = error.message;
        setOfferBusy(button, false, 'Adding...');
      }
    });
  };

  const init = () => {
    document.documentElement.classList.add('g4-inner-ready');
    initWinnerVideoDialog();
    initPhotoViewer();
    initCartUpgrade();
    initCartAddon();

    const cart = document.querySelector('#Cart');
    if (cart) {
      const announceCartUpdate = () => {
        cart.setAttribute('aria-busy', 'true');
        window.setTimeout(() => cart.removeAttribute('aria-busy'), 900);
      };
      cart.querySelectorAll('quantity-selector button, .remove').forEach((control) => {
        control.addEventListener('click', announceCartUpdate);
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
